import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { retrieveProfileData, updateMoneyChitStatus, updateInstallmentMoneyChitPayment, updateMoneyChitSettlement } from "../../api/PupilApi";

// UpdateStatusForm component for Personal Loan
function UpdateStatusForm({ loan, onSave, onCancel }) {
  const [status, setStatus] = useState(loan.personalLoanStatus || "pending");

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm">
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <div className="flex-grow w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">Update Loan Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
          >
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="closed">Closed</option>
            <option value="settled">Settled</option>
          </select>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex-grow sm:flex-grow-0"
            onClick={() => onSave(status)}
          >
            💾 Save
          </button>
          <button
            className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex-grow sm:flex-grow-0"
            onClick={onCancel}
          >
            ✕ Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// SettlementStatusForm component for Personal Loan
function SettlementStatusForm({ loan, onSave, onCancel }) {
  const [settlementData, setSettlementData] = useState({
    status: loan.settlementStatus || "fullSettlement",
    settlementDate: new Date().toISOString().split('T')[0],
    notes: '',
    paidAmount: loan.remainingBalance || '',
    proofLink: loan.proofLink || ''
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'paidAmount') {
      const amount = parseFloat(value) || 0;
      if (amount > loan.loanAmount) {
        setError(`Settlement amount cannot exceed loan amount of ₹${loan.loanAmount}`);
      } else {
        setError('');
      }
    }
    
    setSettlementData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (settlementData.paidAmount && parseFloat(settlementData.paidAmount) > loan.loanAmount) {
      setError(`Settlement amount cannot exceed loan amount of ₹${loan.loanAmount}`);
      return;
    }
    setError('');
    onSave(settlementData);
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl border border-orange-100 shadow-sm mt-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
        <h4 className="font-semibold text-lg text-gray-800">Update Loan Settlement</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Status</label>
          <select
            name="status"
            value={settlementData.status}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          >
            <option value="fullSettlement">Full Settlement</option>
            <option value="partialSettlement">Partially Settled</option>
            <option value="cancelled">Cancelled</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Date</label>
          <input
            type="date"
            name="settlementDate"
            value={settlementData.settlementDate}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Amount (Max: ₹{loan.loanAmount})</label>
          <input
            type="number"
            name="paidAmount"
            value={settlementData.paidAmount}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Settlement Amount"
            max={loan.loanAmount}
          />
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <input
            type="text"
            name="notes"
            value={settlementData.notes}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Additional notes..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Proof Document Link</label>
          <input
            type="text"
            name="proofLink"
            value={settlementData.proofLink}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Link to proof document..."
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-medium flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={!!error}
        >
          💾 Save Settlement
        </button>
        <button
          className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-4 py-2 rounded-lg font-medium flex-1"
          onClick={onCancel}
        >
          ✕ Cancel
        </button>
      </div>
    </div>
  );
}

// EMIEditForm component for Personal Loan
function EMIEditForm({ installment, onSave, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    installmentNumber: installment.installmentNumber,
    paidAmount: installment.paidAmount || installment.emi || installment.monthlyEMI || 0,
    modeOfTransaction: installment.modeOfTransaction || 'cash',
    paidDate: installment.paidDate || '',
    transactionId: installment.transactionId || '',
    emiDate: installment.emiDate || formatDueDate(installment.dueDate) || '',
  });

  // Helper function to format due date array to string
  function formatDueDate(dueDateArray) {
    if (!dueDateArray || !Array.isArray(dueDateArray)) return '';
    return `${dueDateArray[0]}-${String(dueDateArray[1]).padStart(2, '0')}-${String(dueDateArray[2]).padStart(2, '0')}`;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving EMI payment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-200 shadow-lg mt-4" onSubmit={handleSubmit}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></div>
        <h4 className="font-semibold text-lg text-gray-800">Edit EMI #{installment.installmentNumber}</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Paid Amount</label>
          <input
            name="paidAmount"
            type="number"
            min="0"
            value={formData.paidAmount}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Payment Method</label>
          <select
            name="modeOfTransaction"
            value={formData.modeOfTransaction}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          >
            <option value="cash">Cash</option>
            <option value="accountpay">Account Pay</option>
            <option value="creditcard">Credit Card</option>
            <option value="wallet">Wallet</option>
            <option value="upi">UPI</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">EMI Date</label>
          <input
            name="emiDate"
            type="date"
            value={formData.emiDate}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Paid Date</label>
          <input
            name="paidDate"
            type="date"
            value={formData.paidDate}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
          <input
            name="transactionId"
            type="text"
            value={formData.transactionId}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button
          type="submit"
          disabled={loading}
          className={`bg-gradient-to-r from-green-500 to-green-600 
                      hover:from-green-600 hover:to-green-700 
                      text-white px-6 py-3 rounded-xl font-semibold 
                      shadow-md hover:shadow-lg transition-all duration-200 flex-grow
                      ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {loading ? "⏳ Saving..." : "💳 Save EMI Payment"}
        </button>
        <button
          type="button"
          className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow"
          onClick={onCancel}
        >
          ↶ Cancel
        </button>
      </div>
    </form>
  );
}

const ProfileViewPersonalLoan = () => {
  const { email } = useParams();
  const [searchParams] = useSearchParams();
  const loanId = searchParams.get('loanId');
  const loanTitle = searchParams.get('loanTitle');
  const loanAmount = searchParams.get('loanAmount');
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editLoanId, setEditLoanId] = useState(null);
  const [selectedLoanForPayment, setSelectedLoanForPayment] = useState(null);
  const [editInstallment, setEditInstallment] = useState(null);
  const [settlementStatus, setSettlementStatus] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!email) return;
    
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const res = await retrieveProfileData(email);
        setProfile(res.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch profile data.");
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [email]);

  // Helper function to format due date array to string
  const formatDueDate = (dueDateArray) => {
    if (!dueDateArray || !Array.isArray(dueDateArray)) return '-';
    return `${dueDateArray[0]}-${String(dueDateArray[1]).padStart(2, '0')}-${String(dueDateArray[2]).padStart(2, '0')}`;
  };

  // Process personal loan data - UPDATED VERSION
  const processPersonalLoans = (profileData) => {
    if (!profileData) return [];

    // If we have personalLoanDetails array, use that
    if (profileData.personalLoanDetails && Array.isArray(profileData.personalLoanDetails)) {
      return profileData.personalLoanDetails;
    }

    // If we have personalLoanData object with nested array (your JSON structure)
    if (profileData.personalLoanData && Array.isArray(profileData.personalLoanData)) {
      return profileData.personalLoanData.map(loan => ({
        personalLoanId: loan.personalLoanId,
        personalLoanStatus: loan.personalLoanStatus,
        loanTitle: loan.loanTitle,
        loanAmount: loan.loanAmount,
        loanType: loan.loanType,
        loanTenure: loan.loanTenure,
        interestRate: loan.interestRate,
        monthlyEMI: loan.monthlyEMI,
        startDate: loan.startDate,
        endDate: loan.endDate,
        location: loan.location,
        processingFee: loan.processingFee,
        processingFeeAmount: loan.processingFeeAmount,
        totalInterest: loan.totalInterest,
        totalAmount: loan.totalAmount,
        remainingBalance: loan.remainingBalance,
        totalPaid: loan.totalPaid,
        loanInstallmentDetails: (loan.loanInstallmentDetails || []).map(installment => ({
          ...installment,
          formattedDueDate: formatDueDate(installment.dueDate)
        })),
        settlementStatus: loan.settlementStatus,
        settlements: loan.settlements || [],
        // Map additional fields from your JSON
        emi: loan.emi,
        emiType: loan.emiType,
        interest: loan.interest,
        principal: loan.principal,
        month: loan.month
      }));
    }

    // If personalLoanData is a single object (not array)
    if (profileData.personalLoanData && typeof profileData.personalLoanData === 'object' && !Array.isArray(profileData.personalLoanData)) {
      const loan = profileData.personalLoanData;
      return [{
        personalLoanId: loan.personalLoanId,
        personalLoanStatus: loan.personalLoanStatus,
        loanTitle: loan.loanTitle,
        loanAmount: loan.loanAmount,
        loanType: loan.loanType,
        loanTenure: loan.loanTenure,
        interestRate: loan.interestRate,
        monthlyEMI: loan.monthlyEMI,
        startDate: loan.startDate,
        endDate: loan.endDate,
        location: loan.location,
        processingFee: loan.processingFee,
        processingFeeAmount: loan.processingFeeAmount,
        totalInterest: loan.totalInterest,
        totalAmount: loan.totalAmount,
        remainingBalance: loan.remainingBalance,
        totalPaid: loan.totalPaid,
        loanInstallmentDetails: (loan.loanInstallmentDetails || []).map(installment => ({
          ...installment,
          formattedDueDate: formatDueDate(installment.dueDate)
        })),
        settlementStatus: loan.settlementStatus,
        settlements: loan.settlements || [],
        // Map additional fields from your JSON
        emi: loan.emi,
        emiType: loan.emiType,
        interest: loan.interest,
        principal: loan.principal,
        month: loan.month
      }];
    }

    return [];
  };

  // Get processed personal loans
  const allPersonalLoans = processPersonalLoans(profile);

  // Filter personal loans by loan amount if specified
  const filteredPersonalLoans = allPersonalLoans.filter(loan => {
    if (!loanAmount) return true;
    const planLoanAmount = loan.loanAmount;
    const targetLoanAmount = parseInt(loanAmount);
    return planLoanAmount === targetLoanAmount;
  });

  // Group personal loans by loanId to handle duplicates
  const groupedPersonalLoans = filteredPersonalLoans.reduce((acc, loan) => {
    const loanId = loan.personalLoanId;
    if (!acc[loanId]) {
      acc[loanId] = loan;
    }
    return acc;
  }, {});

  // Convert back to array for rendering
  const uniquePersonalLoans = Object.values(groupedPersonalLoans);

  const handleStatusUpdate = async (loanId, newStatus) => {
    try {
      setUpdatingStatus(true);
      await updateMoneyChitStatus(email, loanId, newStatus);

      setProfile((prev) => ({
        ...prev,
        personalLoanDetails: prev.personalLoanDetails ? prev.personalLoanDetails.map((loan) =>
          loan.personalLoanId === loanId ? { ...loan, personalLoanStatus: newStatus } : loan
        ) : [],
        personalLoanData: prev.personalLoanData ? (
          Array.isArray(prev.personalLoanData) 
            ? prev.personalLoanData.map(loan => 
                loan.personalLoanId === loanId ? { ...loan, personalLoanStatus: newStatus } : loan
              )
            : prev.personalLoanData.personalLoanId === loanId 
              ? { ...prev.personalLoanData, personalLoanStatus: newStatus }
              : prev.personalLoanData
        ) : prev.personalLoanData
      }));
      
      setEditLoanId(null);
    } catch (err) {
      console.error(err);
      alert("Failed to update loan status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentUpdate = async (loanId, installmentData) => {
    try {
      const paymentRequest = {
        email,
        loanId,
        installmentNumber: installmentData.installmentNumber,
        paidAmount: installmentData.paidAmount,
        paidFrom: installmentData.modeOfTransaction,
        paidDate: installmentData.paidDate,
        emiDate: installmentData.emiDate,
        transactionId: installmentData.transactionId,
      };

      await updateInstallmentMoneyChitPayment(paymentRequest);
      const res = await retrieveProfileData(email);
      setProfile(res.data);
      setEditInstallment(null);
      setSelectedLoanForPayment(null);
    } catch (err) {
      alert("Failed to update EMI payment");
    }
  };

  const handleSettlementUpdate = async (loanId, settlementData) => {
    try {
      const settlementRequest = {
        status: settlementData.status,
        settlementDate: settlementData.settlementDate,
        notes: settlementData.notes,
        paidAmount: parseFloat(settlementData.paidAmount),
        proofLink: settlementData.proofLink,
        loanId: loanId,
        email: email
      };

      await updateMoneyChitSettlement(settlementRequest);
      const res = await retrieveProfileData(email);
      setProfile(res.data);
      setSettlementStatus(null);
    } catch (err) {
      console.error('Error updating settlement:', err);
      alert('Failed to update settlement status');
    }
  };

  // Helper function to check if loan status is active/approved
  const isLoanActive = (loan) => {
    const status = loan.personalLoanStatus || '';
    return status.toLowerCase() === 'active' || status.toLowerCase() === 'approved';
  };

  // Helper function to check if loan status is pending
  const isLoanPending = (loan) => {
    const status = loan.personalLoanStatus || '';
    return status.toLowerCase() === 'pending' || status.toLowerCase() === 'requested' || !status;
  };

  // Helper function to check if settlement is completed
  const isSettlementCompleted = (loan) => {
    const settlementStatus = loan.settlementStatus || '';
    return settlementStatus.toLowerCase() === 'fullsettlement' || 
           settlementStatus.toLowerCase() === 'settled' ||
           settlementStatus.toLowerCase() === 'closed';
  };

  // Helper function to get settlement status badge
  const getSettlementStatusBadge = (status) => {
    const statusConfig = {
      'requested': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'approved': 'bg-blue-100 text-blue-800 border border-blue-200',
      'settled': 'bg-green-100 text-green-800 border border-green-200',
      'rejected': 'bg-red-100 text-red-800 border border-red-200',
      'cancelled': 'bg-gray-100 text-gray-800 border border-gray-200',
      'fullSettlement': 'bg-green-100 text-green-800 border border-green-200',
      'partialSettlement': 'bg-blue-100 text-blue-800 border border-blue-200',
      'closed': 'bg-gray-100 text-gray-800 border border-gray-200',
      'active': 'bg-green-100 text-green-800 border border-green-200'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig[status] || 'bg-gray-100'}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Function to display settlement details
  const renderSettlementDetails = (loan) => {
    if (!loan.settlements || loan.settlements.length === 0) return null;

    const settlement = loan.settlements[0];
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
          <h4 className="font-semibold text-gray-800">Settlement Details</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="text-sm text-gray-600">Settlement Date</div>
            <div className="font-medium text-gray-800">{settlement.settlementDate}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Settlement Amount</div>
            <div className="font-medium text-green-600">₹{settlement.paidAmount}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Processed By</div>
            <div className="font-medium text-gray-800">{settlement.processedBy}</div>
          </div>
        </div>
        {settlement.notes && (
          <div className="mt-2">
            <div className="text-sm text-gray-600">Notes</div>
            <div className="font-medium text-gray-800">{settlement.notes}</div>
          </div>
        )}
      </div>
    );
  };

  // Get loan amount display name
  const getLoanAmountDisplay = (amount) => {
    if (amount >= 100000) return `${amount/100000} Lakh${amount > 100000 ? 's' : ''} Loan`;
    if (amount >= 1000) return `${amount/1000}K Loan`;
    return `${amount} Loan`;
  };

  // Calculate loan progress
  const calculateLoanProgress = (loan) => {
    const total = loan.totalAmount || loan.loanAmount;
    const paid = loan.totalPaid || 0;
    const progress = total > 0 ? (paid / total) * 100 : 0;
    return {
      progress: Math.min(progress, 100),
      paid: paid,
      remaining: total - paid
    };
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Loading profile data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center">
      <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-red-200 max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">⚠️</span>
        </div>
        <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl shadow-2xl overflow-hidden mb-8">
          <div className="p-6 sm:p-8 text-white">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl lg:text-4xl font-bold mb-1">{profile.name || "Profile"}</h1>
                <p className="text-blue-100 text-lg">Personal Loan Management</p>
                {loanAmount && (
                  <div className="mt-2">
                    <p className="text-blue-200 text-sm">
                      Viewing: <span className="font-semibold">{getLoanAmountDisplay(parseInt(loanAmount))}</span>
                    </p>
                    {loanTitle && (
                      <p className="text-blue-200 text-xs">Loan Title: {loanTitle}</p>
                    )}
                    <p className="text-blue-200 text-xs mt-1">
                      Showing {uniquePersonalLoans.length} loan{uniquePersonalLoans.length !== 1 ? 's' : ''} for this loan type
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl w-full sm:w-auto text-center"
                >
                  ← Back to Dashboard
                </button>
                <button
                  onClick={() => navigate('/personal-loan-dashboard')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl w-full sm:w-auto text-center"
                >
                  📊 All Loans
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-800">Profile Information</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
              <div className="text-sm text-gray-600 font-medium">Email</div>
              <div className="text-lg font-semibold text-gray-800 truncate">{profile.pupilEmail}</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
              <div className="text-sm text-gray-600 font-medium">Mobile</div>
              <div className="text-lg font-semibold text-gray-800">{profile.pupilMobile}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-100">
              <div className="text-sm text-gray-600 font-medium">Status</div>
              <div className="text-lg font-semibold text-gray-800 capitalize">{profile.status}</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl border border-orange-100">
              <div className="text-sm text-gray-600 font-medium">Role</div>
              <div className="text-lg font-semibold text-gray-800 capitalize">{profile.role}</div>
            </div>
          </div>
        </div>

        {/* Personal Loans Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="w-3 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-800">
              {uniquePersonalLoans.length === 1 ? 'Personal Loan Details' : 'Personal Loans'}
            </h2>
            <span className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              {uniquePersonalLoans.length} Loan{uniquePersonalLoans.length !== 1 ? 's' : ''}
            </span>
            {loanAmount && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                parseInt(loanAmount) === 100000 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                  : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
              }`}>
                {getLoanAmountDisplay(parseInt(loanAmount))}
              </span>
            )}
          </div>

          {uniquePersonalLoans.length > 0 ? (
            <div className="grid gap-6">
              {uniquePersonalLoans.map((loan) => {
                const isActive = isLoanActive(loan);
                const isPending = isLoanPending(loan);
                const hasSettlement = loan.settlements && loan.settlements.length > 0;
                const isSettled = isSettlementCompleted(loan);
                const loanDisplayName = getLoanAmountDisplay(loan.loanAmount);
                const loanProgress = calculateLoanProgress(loan);
                
                return (
                  <div key={loan.personalLoanId} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 hover:border-blue-300 transition-all duration-300 shadow-sm hover:shadow-md">
                    {/* Loan Header */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{loan.loanTitle || loanDisplayName}</h3>
                          <p className="text-gray-600">Loan ID: {loan.personalLoanId} • {loanDisplayName}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-start lg:items-end gap-2 mt-2 lg:mt-0">
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${
                          isActive ? 'bg-green-100 text-green-800 border border-green-200' : 
                          isPending ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 
                          'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {loan.personalLoanStatus || "-"}
                        </span>
                        {loan.settlementStatus && (
                          <div className="text-sm">
                            <span className="text-gray-600 mr-2">Settlement:</span>
                            {getSettlementStatusBadge(loan.settlementStatus)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Loan Progress */}
                    <div className="bg-white p-4 rounded-xl border border-gray-200 mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Repayment Progress</span>
                        <span className="text-sm font-semibold text-blue-600">
                          ₹{loanProgress.paid.toLocaleString()} of ₹{loan.totalAmount?.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-teal-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${loanProgress.progress}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{loanProgress.progress.toFixed(1)}% Complete</span>
                        <span>₹{loanProgress.remaining.toLocaleString()} remaining</span>
                      </div>
                    </div>

                    {/* Loan Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Loan Amount</div>
                        <div className="text-lg font-bold text-blue-600">₹{loan.loanAmount?.toLocaleString()}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Tenure</div>
                        <div className="text-lg font-bold text-green-600">{loan.loanTenure} Months</div>
                      </div>
                      {/* <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Interest Rate</div>
                        <div className="text-lg font-bold text-purple-600">{loan.interestRate}%</div>
                      </div> */}
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Monthly EMI</div>
                        <div className="text-lg font-bold text-orange-600">₹{loan.monthlyEMI?.toLocaleString()}</div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-3 rounded-lg border border-purple-200">
                        <div className="text-sm text-gray-600 font-medium">Total Paid</div>
                        <div className="text-lg font-bold text-purple-600">₹{loan.totalPaid?.toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Additional Loan Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Start Date</div>
                        <div className="text-lg font-bold text-indigo-600">{loan.startDate}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">End Date</div>
                        <div className="text-lg font-bold text-red-600">{loan.endDate}</div>
                      </div>
                      
                    </div>

                    {/* Financial Breakdown */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {/* <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200">
                        <div className="text-sm text-gray-600 font-medium">Total Interest</div>
                        <div className="text-lg font-bold text-cyan-600">₹{loan.totalInterest?.toLocaleString()}</div>
                      </div> */}
                      {/* <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                        <div className="text-sm text-gray-600 font-medium">Total Amount</div>
                        <div className="text-lg font-bold text-emerald-600">₹{loan.totalAmount?.toLocaleString()}</div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200">
                        <div className="text-sm text-gray-600 font-medium">Remaining Balance</div>
                        <div className="text-lg font-bold text-amber-600">₹{loan.remainingBalance?.toLocaleString()}</div>
                      </div> */}
                      {/* <div className="bg-gradient-to-br from-red-50 to-pink-50 p-3 rounded-lg border border-red-200">
                        <div className="text-sm text-gray-600 font-medium">Processing Fee</div>
                        <div className="text-lg font-bold text-pink-600">₹{loan.processingFeeAmount?.toLocaleString()}</div>
                      </div> */}
                    </div>

                    {/* Settlement Details */}
                    {hasSettlement && renderSettlementDetails(loan)}

                    {/* Action Buttons */}
                    {editLoanId === loan.personalLoanId ? (
                      <UpdateStatusForm
                        loan={loan}
                        onSave={(newStatus) => handleStatusUpdate(loan.personalLoanId, newStatus)}
                        onCancel={() => setEditLoanId(null)}
                      />
                    ) : settlementStatus === loan.personalLoanId ? (
                      <SettlementStatusForm
                        loan={loan}
                        onSave={(settlementData) => handleSettlementUpdate(loan.personalLoanId, settlementData)}
                        onCancel={() => setSettlementStatus(null)}
                      />
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-3">
                        {/* Show Update Status button only for pending loans */}
                        {isPending && (
                          <button 
                            disabled={updatingStatus}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setEditLoanId(loan.personalLoanId)}
                          >
                            {updatingStatus ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Updating...
                              </>
                            ) : (
                              <>
                                <span>⚙️</span> Update Status
                              </>
                            )}
                          </button>
                        )}
                        
                        {/* Show Manage EMI and Update Settlement buttons only for active loans that are NOT settled */}
                        {isActive && !isSettled && (
                          <>
                            {loan.loanInstallmentDetails && loan.loanInstallmentDetails.length > 0 && (
                              <button 
                                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2"
                                onClick={() => setSelectedLoanForPayment(loan)}
                              >
                                <span>💳</span> Manage EMI Payments
                              </button>
                            )}
                            
                            <button 
                              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2"
                              onClick={() => setSettlementStatus(loan.personalLoanId)}
                            >
                              <span>💳</span> {hasSettlement ? 'Update Settlement' : 'Loan Settlement'}
                            </button>
                          </>
                        )}
                        
                        {/* Show only Manage EMI button for active loans that ARE settled */}
                        {isActive && isSettled && (
                          <>
                            {loan.loanInstallmentDetails && loan.loanInstallmentDetails.length > 0 && (
                              <button 
                                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2"
                                onClick={() => setSelectedLoanForPayment(loan)}
                              >
                                <span>💳</span> Manage EMI Payments
                              </button>
                            )}
                          </>
                        )}
                        
                        {/* Show no actions for other statuses */}
                        {!isPending && !isActive && (
                          <div className="text-center py-3 text-gray-500 flex-grow">
                            Loan status: {loan.personalLoanStatus}. No actions available.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">💰</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {loanAmount ? `No Matching Loan Found` : `No Personal Loans Found`}
              </h3>
              <p className="text-gray-500 mb-4">
                {loanAmount 
                  ? `No personal loan found matching ${getLoanAmountDisplay(parseInt(loanAmount))} for this user.`
                  : "This profile doesn't have any personal loans yet."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/personal-loan-dashboard')}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                >
                  ← Back to Loan Dashboard
                </button>
                {loanAmount && (
                  <button
                    onClick={() => {
                      // Clear the filter to show all personal loans
                      navigate(location.pathname);
                    }}
                    className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                  >
                    View All Loans
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* EMI Payment Modal */}
        {selectedLoanForPayment && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-green-500 to-teal-600 p-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold">EMI Payment Management</h3>
                    <p className="text-green-100">
                      {selectedLoanForPayment.loanTitle || getLoanAmountDisplay(selectedLoanForPayment.loanAmount)}
                    </p>
                    <p className="text-green-200 text-sm">Loan ID: {selectedLoanForPayment.personalLoanId}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedLoanForPayment(null);
                      setEditInstallment(null);
                    }}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all duration-200"
                  >
                    <span className="text-xl">✕</span>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto flex-grow p-4 sm:p-6">
                {selectedLoanForPayment.loanInstallmentDetails && selectedLoanForPayment.loanInstallmentDetails.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden lg:block">
                      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                              <th className="p-4 text-left font-semibold text-gray-700">#</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Due Date</th>
                              <th className="p-4 text-left font-semibold text-gray-700">EMI Amount</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Principal</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Interest</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Remaining Balance</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Status</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedLoanForPayment.loanInstallmentDetails.map((inst, idx) => (
                              <React.Fragment key={idx}>
                                <tr className={`hover:bg-gray-50 transition-colors ${editInstallment?.installmentNumber === inst.installmentNumber ? "bg-blue-50" : ""}`}>
                                  <td className="p-4 font-medium text-gray-900">{inst.installmentNumber}</td>
                                  <td className="p-4 text-gray-700">{inst.formattedDueDate}</td>
                                  <td className="p-4 font-semibold text-green-600">₹{inst.emi || inst.monthlyEMI || 0}</td>
                                  <td className="p-4 font-semibold text-blue-600">₹{inst.principal || 0}</td>
                                  <td className="p-4 font-semibold text-orange-600">₹{inst.interest || 0}</td>
                                  <td className="p-4 font-semibold text-purple-600">₹{inst.remainingBalance || 0}</td>
                                  <td className="p-4">
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                      inst.status === 'Paid' ? 'bg-green-100 text-green-800' : 
                                      inst.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                      {inst.status || 'Pending'}
                                    </span>
                                  </td>
                                  <td className="p-4">
                                    <button
                                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${editInstallment?.installmentNumber === inst.installmentNumber ? "bg-red-500 hover:bg-red-600 text-white" : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"}`}
                                      onClick={() => setEditInstallment(editInstallment?.installmentNumber === inst.installmentNumber ? null : inst)}
                                    >
                                      {editInstallment?.installmentNumber === inst.installmentNumber ? "Cancel" : "Edit"}
                                    </button>
                                  </td>
                                </tr>
                                {editInstallment?.installmentNumber === inst.installmentNumber && (
                                  <tr className="bg-blue-50">
                                    <td colSpan="8" className="p-4">
                                      <EMIEditForm
                                        installment={editInstallment}
                                        onSave={(formData) => handlePaymentUpdate(selectedLoanForPayment.personalLoanId, formData)}
                                        onCancel={() => setEditInstallment(null)}
                                      />
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Cards */}
                    <div className="lg:hidden space-y-4">
                      {selectedLoanForPayment.loanInstallmentDetails.map((inst, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-5 border border-gray-200">
                          <div className="flex flex-col gap-2 mb-4">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold text-gray-800">EMI #{inst.installmentNumber}</span>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                inst.status === 'Paid' ? 'bg-green-100 text-green-800' : 
                                inst.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {inst.status || 'Pending'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">Due Date: {inst.formattedDueDate}</div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">EMI Amount</div>
                              <div className="font-bold text-green-600">₹{inst.emi || inst.monthlyEMI || 0}</div>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">Principal</div>
                              <div className="font-bold text-blue-600">₹{inst.principal || 0}</div>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">Interest</div>
                              <div className="font-bold text-orange-600">₹{inst.interest || 0}</div>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">Remaining Balance</div>
                              <div className="font-bold text-purple-600">₹{inst.remainingBalance || 0}</div>
                            </div>
                          </div>

                          <button
                            className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 ${editInstallment?.installmentNumber === inst.installmentNumber ? "bg-red-500 hover:bg-red-600 text-white" : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"}`}
                            onClick={() => setEditInstallment(editInstallment?.installmentNumber === inst.installmentNumber ? null : inst)}
                          >
                            {editInstallment?.installmentNumber === inst.installmentNumber ? "✕ Cancel Edit" : "✏️ Edit EMI Payment"}
                          </button>

                          {editInstallment?.installmentNumber === inst.installmentNumber && (
                            <div className="mt-4">
                              <EMIEditForm
                                installment={editInstallment}
                                onSave={(formData) => handlePaymentUpdate(selectedLoanForPayment.personalLoanId, formData)}
                                onCancel={() => setEditInstallment(null)}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">💸</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No EMI Records Found</h3>
                    <p className="text-gray-500">This loan doesn't have any EMI payment records yet.</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  onClick={() => {
                    setSelectedLoanForPayment(null);
                    setEditInstallment(null);
                  }}
                >
                  Close EMI Manager
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileViewPersonalLoan;